/**
@format
@flow
*/
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import ActionTypes from '@ziynx/action-types';
import { Button } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import Image from '@ziynx/components/images';
import Input from '@ziynx/components/inputs';
import { withAppManager } from '@ziynx/components/utilities';
import { Form, FormRow } from '@ziynx/components/forms';
import Link from '@ziynx/components/links';
import { Body } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import Dropdown from '@ziynx/components/selects';
import type { CompanyState } from '@ziynx/flow-types/companyProfile';
import type { AuthState } from '@ziynx/flow-types/auth';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import { INDUSTRIES, COMPANY_SIZE_OPTIONS } from '../../constants';
import ziynxLogo from '../../assets/ZIYNX_LOGO_2.png';
import logoSmall from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|

|}>;

type FormData = {
  displayName: string,
  selectIndustry?: string,
  companySize: string,
};

type DispatchType = ({
  type: string,
  body?: { name: string },
}) => void;

type HistoryStack = {
  replace: (pathName: string) => void,
  push: (pathName: string) => void,
}

const StyledButton = styled(Button)`
  margin-bottom: 16px;
`;

const FormSectionWrapper = styled.div`
  display: table;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '0px 16px 48px' : '0px 40px 48px')};
  ${({ isMobile }) => !isMobile && 'margin: 0 auto;'}
`;

const LogoWrapper = styled.div`
  padding-top: ${({ isMobile }) => (isMobile ? '32px' : '48px')};
  padding-bottom: 64px;
`;

const SubtitleWrapper = styled.div`
  padding-bottom: ${({ isMobileSmall }) => (isMobileSmall ? '48px' : '56px')} ;
`;

const StyledFormRow = styled(FormRow)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const inputsData = {
  data: [
    {
      id: '1',
      name: 'displayName',
      defaultValue: '',
      regExPattern: undefined,
      errorMessage: undefined,
    },
    {
      id: '2',
      name: 'selectIndustry',
      defaultValue: 'Select in',
      regExPattern: undefined,
      errorMessage: undefined,
    },
    {
      id: '3',
      name: 'companySize',
      defaultValue: '',
      regExPattern: undefined,
      errorMessage: undefined,
    },
  ],
};

const ProfileSetup = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;
  const {
    handleSubmit, control, formState,
  } = useForm();

  const isMobileSmall = viewport === 'mobileSmall';
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';

  // Set up dispatch
  const dispatch: DispatchType = useDispatch();

  const _setupCompanyService = ({ company }) => company?.me ?? {};

  const companyService: CompanyState = useSelector(_setupCompanyService, shallowEqual);

  const authService: AuthState = useSelector(
    ({ auth }) => auth[ActionTypes.auth.checkIfLoggedIn.GROUP] || {},
    shallowEqual,
  );

  // Nav stack
  const history: HistoryStack = useHistory();

  // Shows if waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);

  // Sets error state for empty select
  const [emptyError, setEmptyError] = useState(false);

  // Fields to watch for changes or errors
  const { dirtyFields } = formState;

  // RE-ROUTE TO SIGN-UP IF NOT LOGGED IN
  useEffect(() => {
    if (!authService.isLoggedIn) {
      history.replace('/sign-up');
    }
  }, []);

  // Log out
  const logOut = () => {
    dispatch({ type: ActionTypes.auth.logout.START });
  };

  // RE-ROUTE TO HOME SCREEN AFTER SUCCESSFULLY POST COMPANY
  useEffect(() => {
    if (companyService?.success) {
      history.push('/company-profile');
      setIsLoading(false);
    }
  }, [companyService?.success]);

  const onSubmit = (data: FormData): void => {
    setIsLoading(true);
    dispatch({
      type: ActionTypes.company.post.START,
      body: {
        name: data.displayName,
        industries: [data.selectIndustry],
        companySize: data.companySize,
      },
    });
  };

  /**
   * Function to render Inputs for Controller render prop
   * of useForm hook
   */
  const renderInputs = ({
    onChange, onBlur, name,
  }: Object): React$Node => {
    let label = '';
    let activeLabel = '';
    let options = [];
    const variant = 'text'; // default type of inputs
    switch (name) {
    case 'displayName':
      label = 'Company display name';
      break;
    case 'companySize':
      label = 'Select no. of employees';
      activeLabel = 'No. of employees';
      options = COMPANY_SIZE_OPTIONS;
      break;
    case 'selectIndustry':
    default:
      label = 'Select industry';
      activeLabel = 'Industry';
      options = INDUSTRIES;
      break;
    }

    /**
     * Set value of input
     */
    const onInputChange = (event: string): void => {
      if (event === '' || undefined) setEmptyError(true);
      else setEmptyError(false);
      onChange(event);
    };

    if (name === 'displayName') {
      return (
        <Input
          variant={variant}
          inputName={name}
          label={label}
          onChange={onInputChange}
          onBlur={onBlur}
          required
        />
      );
    }
    return (
      <Dropdown
        inputName={name}
        activeLabel={activeLabel}
        label={label}
        options={options}
        onChange={onInputChange}
        onBlur={onBlur}
        required
      />
    );
  };

  const renderInputsControllers = (): React$Node => inputsData.data.map((field) => (
    <FormRow key={field?.id} paddingBottom={field?.name === 'selectIndustry' ? '40px' : '32px'}>
      <Controller
        control={control}
        render={renderInputs}
        name={field.name}
        defaultValue={field.defaultValue}
        rules={{
          required: 'Please enter a value to continue',
        }}
      />
    </FormRow>
  ));

  /**
   * To render ziynx logo as image
   */
  const renderLogo = (): React$Node => {
    if (isMobile) {
      return (
        <Image src={logoSmall} width="24px" height="24px" alt="company-logo" />
      );
    }
    return (
      <Image src={ziynxLogo} width="114px" height="24px" alt="company-logo" />
    );
  };

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */

  const isDisabled = (): boolean => {
    if (!inputsData?.data || !dirtyFields || isLoading || emptyError) return true;
    return Object.keys(dirtyFields).length !== inputsData?.data?.length;
  };

  return (
    <>
      <Helmet>
        <title>Sign Up | ZIYNX</title>
      </Helmet>
      <ContentContainer isVertical offsetTop={0}>
        <FormSectionWrapper
          isMobileSmall={isMobileSmall}
          isMobile={isMobile}
        >
          <LogoWrapper isMobile={isMobile}>
            {renderLogo()}
          </LogoWrapper>
          <Form
            formID="profile-setup"
            title="Profile Set Up"
            headingPaddingBottom="12px"
          >
            <SubtitleWrapper isMobileSmall={isMobileSmall}>
              <Body medium color={Colors.GrayDark}>
                We just need a few more details from you to set up your account.
              </Body>
            </SubtitleWrapper>
            {renderInputsControllers()}
            <StyledFormRow height="100%">
              <StyledButton
                disabled={isDisabled()}
                isLoading={isLoading}
                width="100%"
                form="profile-setup"
                type="submit"
                onClick={handleSubmit(onSubmit)}
              >
                {!isMobile ? 'Continue' : 'Submit'}
              </StyledButton>
              <Link variant="text" large onClick={logOut} href="/sign-up">Back to sign up</Link>
            </StyledFormRow>
          </Form>
        </FormSectionWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(ProfileSetup): React$ComponentType<ManagedProps<Props>>);
